import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Alert from "@mui/material/Alert";
import Backdrop from "@mui/material/Backdrop";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import ESign from "../components/common/ESign";
import { get, post } from "../services/api";
import { FIELD_FORMATS } from "../common/fieldFormats";
import { useToast } from "../admin/components/ToastProvider";
import {
  EmployeeErrorState,
  EmployeeLoadingState,
  EmployeePageHeader,
  EmployeeSurface,
} from "../employee/components/EmployeePage";
import { tokens as t } from "../theme/tokens";

const CloseIcon = (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
    <path d="M18 6 6 18M6 6l12 12" />
  </svg>
);

const ViewDocument: React.FC = () => {
  const navigate = useNavigate();
  const params = useParams();
  const { showToast } = useToast();
  const docRef = useRef<HTMLDivElement>(null);
  const [document, setDocument] = useState<any>({});
  const [showESign, setShowESign] = useState(false);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [validationMessage, setValidationMessage] = useState("");
  const docId = localStorage.getItem("signDocId");
  const publicToken = localStorage.getItem("publicToken") || "";

  useEffect(() => {
    window.document.title = "PaperlessNOW : Sign Document";
    if (!publicToken || !docId) {
      setError("This signing session is incomplete or has expired. Return to your employee document link and try again.");
      setLoading(false);
      return;
    }

    get(`employee/document/detail/${docId}`, publicToken)
      .then((response: any) => setDocument(response?.payload?.data || {}))
      .catch(() => setError("We could not load this document. Return to your checklist and try again."))
      .finally(() => setLoading(false));
  }, [docId, publicToken]);

  const saveDocument = () => {
    let elements = docRef.current?.querySelectorAll("table tbody tr td input") || [];
    if (elements.length === 0) elements = docRef.current?.querySelectorAll("input[name]") || [];

    const values: Record<string, string> = {};
    const emptyFields: string[] = [];
    const formatErrors: string[] = [];

    elements.forEach((element) => {
      const input = element as HTMLInputElement;
      const key = input.name;
      const value = input.value;

      if (input.required && !value) emptyFields.push(input.dataset.value || "");

      const formatName = input.dataset.format || (key === "sin_no" ? "sin" : undefined);
      if (formatName && FIELD_FORMATS[formatName] && value) {
        const format = FIELD_FORMATS[formatName];
        const valid = format.validate ? format.validate(value) : format.pattern.test(value);
        if (!valid) formatErrors.push(format.errorMessage);
      }

      input.setAttribute("value", value);
      values[key] = value;
    });

    if (emptyFields.length) {
      const namedFields = emptyFields.filter(Boolean);
      setValidationMessage(
        namedFields.length
          ? `Complete the following required fields before signing: ${namedFields.join(", ")}.`
          : "Complete all required fields before signing this document."
      );
      return null;
    }

    if (formatErrors.length) {
      setValidationMessage(`Please correct the following before signing: ${formatErrors.join(" ")}`);
      return null;
    }

    setValidationMessage("");
    return values;
  };

  const openESign = () => {
    if (saveDocument()) setShowESign(true);
  };

  const saveSignature = (image: string) => {
    const filledFields = saveDocument();
    if (!filledFields || !docId) return;

    setSubmitting(true);
    post("employee/queue", {
      ...document,
      document_filled: docRef.current?.innerHTML,
      data: image,
      id: docId,
      createdAt: new Date().toString(),
      formFields: filledFields,
    })
      .then(() =>
        navigate(`/employee/document-dashboard/${params.token}?access_token=${publicToken}&ref=docsign`)
      )
      .catch(() => {
        setSubmitting(false);
        showToast("We could not save your signature. Please try again.", "error");
      });
  };

  if (loading) return <EmployeeLoadingState message="Preparing your document…" />;
  if (error) return <EmployeeErrorState message={error} />;

  return (
    <>
      <EmployeePageHeader
        eyebrow="Electronic signature"
        title={document.document_name || "Review and sign"}
        description="Complete every required field, review the document, and then add your signature."
      />

      <EmployeeSurface>
        <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: t.color.paper }}>
          <Box
            id="document-container"
            ref={docRef}
            className="employee-document-content"
            dangerouslySetInnerHTML={{ __html: document.document_being_edited_html }}
          />
        </Box>
        <Box sx={{ borderTop: `1px solid ${t.color.cardLine}`, bgcolor: t.color.canvas, p: { xs: 2, sm: 2.5 } }}>
          {validationMessage && (
            <Alert severity="error" sx={{ mb: 2 }} role="alert">
              {validationMessage}
            </Alert>
          )}
          <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
            <Button variant="contained" onClick={openESign} fullWidth={false} sx={{ width: { xs: "100%", sm: "auto" } }}>
              Sign this document
            </Button>
          </Box>
        </Box>
      </EmployeeSurface>

      <Dialog
        open={showESign}
        onClose={() => !submitting && setShowESign(false)}
        maxWidth="sm"
        fullWidth
        aria-labelledby="signature-dialog-title"
      >
        <DialogTitle id="signature-dialog-title" sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          Add your signature
          <IconButton onClick={() => setShowESign(false)} disabled={submitting} aria-label="Close signature dialog">
            {CloseIcon}
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ p: { xs: 2, sm: 3 } }}>
          <ESign saveSignature={saveSignature} isLoading={submitting} />
        </DialogContent>
      </Dialog>

      <Backdrop open={submitting} sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.modal + 1 }}>
        <Box role="status" aria-live="assertive" sx={{ textAlign: "center", maxWidth: 360, px: 3 }}>
          <CircularProgress color="inherit" />
          <Typography sx={{ mt: 1.5 }}>Processing your signature. Do not close this window.</Typography>
        </Box>
      </Backdrop>
    </>
  );
};

export default ViewDocument;
